/**
 * Tipos relacionados con vinculación de dispositivo
 * Sprint 3 - Device Bind
 */

/**
 * Información del dispositivo vinculado
 */
export interface UserDevice {
    id: number;
    device_uuid: string;
    device_platform: 'android' | 'ios' | 'web' | null;
    device_model: string | null;
    app_version: string | null;
    bound_at: string;
    last_seen_at: string;
}

/**
 * Códigos de error específicos de dispositivo
 */
export type DeviceErrorCode =
    | 'DEVICE_BIND_REQUIRED'
    | 'DEVICE_ALREADY_BOUND'
    | 'DEVICE_MISMATCH'
    | 'INVALID_SIGNATURE'
    | 'INVALID_TIMESTAMP'
    | 'ACCOUNT_NOT_VERIFIED'; // Sprint 5

/**
 * Error de API con código de dispositivo
 */
export interface DeviceApiError {
    message: string;
    error_code?: DeviceErrorCode;
    errors?: Record<string, string[]>;
    status?: number;
    data?: {
        bind_nonce?: string;
        nonce_expires_in?: number;
    };
}

/**
 * Credenciales de login con datos de dispositivo
 */
export interface LoginWithDeviceCredentials {
    email: string;
    password: string;
    device_uuid: string;
    device_secret: string;
    timestamp: number;
    device_signature: string;
}

/**
 * Datos para vincular dispositivo
 */
export interface DeviceBindData {
    email: string;
    password: string;
    device_uuid: string;
    device_secret: string;
    nonce: string;
    timestamp: number;
    signature: string;
    device_platform?: 'android' | 'ios' | 'web';
    device_model?: string;
    app_version?: string;
}

/**
 * Respuesta del login cuando requiere bind
 */
export interface DeviceBindRequiredResponse {
    message: string;
    error_code: 'DEVICE_BIND_REQUIRED';
    data: {
        bind_nonce: string;
        nonce_expires_in: number;
    };
}

/**
 * Respuesta exitosa de bind
 */
export interface DeviceBindResponse {
    message: string;
    data: {
        device: UserDevice;
        token: string;
    };
}

/**
 * Respuesta de login exitoso con dispositivo
 */
export interface LoginWithDeviceResponse {
    message: string;
    data: {
        user: {
            id: number;
            name: string;
            display_name?: string | null;
            email: string;
            is_admin: boolean;
            operation_mode: 'client' | 'operator';
            avatar_url?: string | null;
            email_verified_at: string | null;
            created_at: string;
        };
        device: UserDevice;
        token: string;
    };
}

/**
 * Respuesta al obtener info del dispositivo
 */
export interface DeviceInfoResponse {
    data: UserDevice;
}

/**
 * Verifica si un error es de tipo "bind requerido"
 */
export function isDeviceBindRequired(error: unknown): error is DeviceBindRequiredResponse {
    return (
        typeof error === 'object' &&
        error !== null &&
        'error_code' in error &&
        (error as DeviceBindRequiredResponse).error_code === 'DEVICE_BIND_REQUIRED'
    );
}

/**
 * Verifica si un error es de tipo "device mismatch"
 */
export function isDeviceMismatch(error: unknown): boolean {
    return (
        typeof error === 'object' &&
        error !== null &&
        'error_code' in error &&
        (error as DeviceApiError).error_code === 'DEVICE_MISMATCH'
    );
}

/**
 * Verifica si un error es de tipo "device already bound" (vinculado a otra cuenta)
 */
export function isDeviceAlreadyBound(error: unknown): boolean {
    return (
        typeof error === 'object' &&
        error !== null &&
        'error_code' in error &&
        (error as DeviceApiError).error_code === 'DEVICE_ALREADY_BOUND'
    );
}

/**
 * Verifica si un error es relacionado con dispositivo
 */
export function isDeviceError(error: unknown): error is DeviceApiError {
    if (typeof error !== 'object' || error === null) return false;
    const deviceError = error as DeviceApiError;
    return deviceError.error_code !== undefined;
}

/**
 * Verifica si un error es de tipo "cuenta no verificada" (Sprint 5)
 */
export function isAccountNotVerified(error: unknown): boolean {
    return (
        typeof error === 'object' &&
        error !== null &&
        'error_code' in error &&
        (error as DeviceApiError).error_code === 'ACCOUNT_NOT_VERIFIED'
    );
}
